import { useState } from 'react';

import { IconCheckCircle, IconPlus, IconTrash } from '../../../common/icons';
import { embryoStageColor, embryoStageLabel, isFrozen, storageLabel } from '../../../core/constants/transfers';
import { PREG_STATE } from '../../../core/constants/dx';
import RecipientSearch from '../../recipients/forms/search';
import { CorpusLuteumPopover, ObservationsPopover } from './details';

// Grilla única de la TE (modelo de papel: la lista NO muta). Una fila por embrión:
//   · disponible (transferred:false): se asigna receptora + CL/Obs (estado local).
//   · transferida (transferred:true): subrayada en el color de su sesión; receptora
//     editable + eliminar SOLO si la preñez está `open`; CL/Obs editables si la fila
//     trae remarks (sesión enriquecida). El ✓ refleja datos reales (no defaults).
export default function TransferRows({
    rows, farmId,
    onRecipient, onClearRecipient, onCL, onObs, onRemove,
    onEditRecipient, onEditRemarks, onDeleteTransfer,
}) {
    const [pop, setPop] = useState(null); // { key, kind: 'cl' | 'obs' }
    const [replacing, setReplacing] = useState(() => new Set()); // filas transferidas en modo "cambiar receptora"
    const active = pop && rows.find((r) => r.key === pop.key);

    const startReplace = (key) => setReplacing((s) => new Set(s).add(key));
    const stopReplace = (key) => setReplacing((s) => { const n = new Set(s); n.delete(key); return n; });

    const applyPopover = (obj) => {
        if (!active) return;
        const key = pop.kind === 'cl' ? 'corpusLuteum' : 'details';
        if (active.transferred) onEditRemarks?.(active, { ...(active.remarks || {}), [key]: obj });
        else if (pop.kind === 'cl') onCL(active.key, obj);
        else onObs(active.key, obj);
        // el cierre lo hace el propio popover (save → onApply → onClose); no duplicar setPop aquí
    };

    return (
        <div className="transfer-table-wrap">
            <table className="transfer-table">
                <thead>
                    <tr>
                        <th className="transfer-th-num">#</th>
                        <th>Embrión</th>
                        <th>Donadora</th>
                        <th>Toro</th>
                        <th className="transfer-th-recipient">Receptora</th>
                        <th>CL</th>
                        <th>Obs</th>
                        <th aria-label="Acciones" />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => {
                        const e = r.embryo || {};
                        const l = e.lineage || {};

                        if (r.transferred) {
                            // CL/Obs editables solo si la fila trae remarks (sesión enriquecida)
                            // Y hay handler para persistirlos: sin onEditRemarks no se muestran
                            // botones que harían un guardado silencioso a la nada.
                            const enriched = r.remarks !== undefined && typeof onEditRemarks === 'function';
                            const hasCL = !!r.remarks?.corpusLuteum;
                            const hasObs = !!r.remarks?.details;
                            const st = PREG_STATE[r.pregnancy?.state];
                            return (
                                <tr key={r.key} className="transfer-tr transfer-tr--done" style={{ '--session': r.color }}>
                                    <td className="transfer-td-num">{i + 1}</td>
                                    <td className="transfer-td-embryo">
                                        <span className="transfer-embryo__code transfer-embryo__code--done">{e.code || '—'}</span>
                                        {(e.stage || isFrozen(e.storage)) && (
                                            <span className="transfer-embryo__chips">
                                                {e.stage && <span className="transfer-stage" style={{ '--stage': embryoStageColor(e.stage) }}>{embryoStageLabel(e.stage)}</span>}
                                                {isFrozen(e.storage) && <span className="transfer-storage transfer-storage--frozen">{storageLabel(e.storage)}</span>}
                                            </span>
                                        )}
                                    </td>
                                    <td className="transfer-td-gen">{l.donor?.name ? <span className="transfer-gen__name">{l.donor.name}</span> : <span className="transfer-done-muted">—</span>}</td>
                                    <td className="transfer-td-gen">{l.sire?.name ? <span className="transfer-gen__name">{l.sire.name}</span> : <span className="transfer-done-muted">—</span>}</td>
                                    <td className="transfer-td-recipient">
                                        {r.editable ? (
                                            replacing.has(r.key) ? (
                                                <RecipientSearch farmId={r.farm?.id} autoFocus onSelect={(rec) => { onEditRecipient?.(r, rec); stopReplace(r.key); }} onClear={() => stopReplace(r.key)} />
                                            ) : (
                                                <RecipientSearch value={r.recipient} farmId={r.farm?.id} onClear={() => startReplace(r.key)} />
                                            )
                                        ) : (
                                            <span className="transfer-edit__locked">
                                                {r.recipient ? `${r.recipient.number}${r.recipient.name ? ` · ${r.recipient.name}` : ''}` : '—'}
                                                {st && <span className="transfer-edit__state">{st.label}</span>}
                                            </span>
                                        )}
                                    </td>
                                    <td className="transfer-td-pop">
                                        {enriched ? (
                                            <button type="button" className={`transfer-pop-btn ${hasCL ? 'transfer-pop-btn--set' : ''}`} onClick={() => setPop({ key: r.key, kind: 'cl' })} title="Cuerpo lúteo">
                                                {hasCL ? <IconCheckCircle /> : <IconPlus />}
                                            </button>
                                        ) : <span className="transfer-done-muted">—</span>}
                                    </td>
                                    <td className="transfer-td-pop">
                                        {enriched ? (
                                            <button type="button" className={`transfer-pop-btn ${hasObs ? 'transfer-pop-btn--set' : ''}`} onClick={() => setPop({ key: r.key, kind: 'obs' })} title="Observaciones">
                                                {hasObs ? <IconCheckCircle /> : <IconPlus />}
                                            </button>
                                        ) : <span className="transfer-done-muted">—</span>}
                                    </td>
                                    <td className="transfer-td-del">
                                        {r.editable && (
                                            <button type="button" className="transfer-row__del" onClick={() => onDeleteTransfer?.(r)} aria-label="Deshacer transferencia" title="Deshacer transferencia">
                                                <IconTrash />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        }

                        // fila disponible (asignable)
                        const donorSub = l.donor?.race?.name || l.donor?.number || '';
                        const sireSub = l.sire?.race?.name || l.sire?.register || '';
                        const hasCL = !!r.remarks?.corpusLuteum;
                        const hasObs = !!r.remarks?.details;
                        return (
                            <tr key={r.key} className="transfer-tr">
                                <td className="transfer-td-num">{i + 1}</td>
                                <td className="transfer-td-embryo">
                                    <span className="transfer-embryo__code">{e.code}</span>
                                    <span className="transfer-embryo__chips">
                                        <span className="transfer-stage" style={{ '--stage': embryoStageColor(e.stage) }}>{embryoStageLabel(e.stage)}</span>
                                        {isFrozen(e.storage) && <span className="transfer-storage transfer-storage--frozen">{storageLabel(e.storage)}</span>}
                                    </span>
                                </td>
                                <td className="transfer-td-gen">
                                    <span className="transfer-gen__name">{l.donor?.name || '—'}</span>
                                    {donorSub && <span className="transfer-gen__sub">{donorSub}</span>}
                                </td>
                                <td className="transfer-td-gen">
                                    <span className="transfer-gen__name">{l.sire?.name || '—'}</span>
                                    {sireSub && <span className="transfer-gen__sub">{sireSub}</span>}
                                </td>
                                <td className="transfer-td-recipient">
                                    <RecipientSearch value={r.recipient} farmId={farmId} onSelect={(rec) => onRecipient(r.key, rec)} onClear={() => onClearRecipient(r.key)} />
                                </td>
                                <td className="transfer-td-pop">
                                    <button type="button" className={`transfer-pop-btn ${hasCL ? 'transfer-pop-btn--set' : ''}`} onClick={() => setPop({ key: r.key, kind: 'cl' })} title="Cuerpo lúteo">
                                        {hasCL ? <IconCheckCircle /> : <IconPlus />}
                                    </button>
                                </td>
                                <td className="transfer-td-pop">
                                    <button type="button" className={`transfer-pop-btn ${hasObs ? 'transfer-pop-btn--set' : ''}`} onClick={() => setPop({ key: r.key, kind: 'obs' })} title="Observaciones">
                                        {hasObs ? <IconCheckCircle /> : <IconPlus />}
                                    </button>
                                </td>
                                <td className="transfer-td-del">
                                    {r.source === 'bank' && (
                                        <button type="button" className="transfer-row__del" onClick={() => onRemove(r.key)} aria-label="Quitar del banco" title="Quitar">
                                            <IconTrash />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {active && pop.kind === 'cl' && (
                <CorpusLuteumPopover index={rows.findIndex((r) => r.key === pop.key) + 1} value={active.remarks?.corpusLuteum} onApply={applyPopover} onClose={() => setPop(null)} />
            )}
            {active && pop.kind === 'obs' && (
                <ObservationsPopover index={rows.findIndex((r) => r.key === pop.key) + 1} value={active.remarks?.details} onApply={applyPopover} onClose={() => setPop(null)} />
            )}
        </div>
    );
}
